import React, { useState, useEffect, useCallback } from 'react';
import { Timer } from 'lucide-react';

/**
 * BookingCountdown — Displays a live MM:SS countdown for a PENDING_LOCK booking.
 *
 * Props:
 *  - lockExpiresAt: ISO string of when the lock expires
 *  - onExpired: callback when timer hits 0
 */
const BookingCountdown = ({ lockExpiresAt, onExpired }) => {
  const calcRemaining = useCallback(() => {
    if (!lockExpiresAt) return 0;
    const diff = new Date(lockExpiresAt).getTime() - Date.now();
    return Math.max(0, Math.floor(diff / 1000));
  }, [lockExpiresAt]);

  const [remaining, setRemaining] = useState(calcRemaining);

  useEffect(() => {
    setRemaining(calcRemaining());
    const id = setInterval(() => {
      const r = calcRemaining();
      setRemaining(r);
      if (r <= 0) {
        clearInterval(id);
        onExpired?.();
      }
    }, 1000);
    return () => clearInterval(id);
  }, [lockExpiresAt, calcRemaining, onExpired]);

  const minutes = String(Math.floor(remaining / 60)).padStart(2, '0');
  const seconds = String(remaining % 60).padStart(2, '0');
  const isUrgent = remaining <= 30;
  const isExpired = remaining <= 0;

  return (
    <div
      data-testid="booking-countdown"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.75rem',
        padding: '1rem 1.5rem',
        borderRadius: '12px',
        background: isExpired
          ? 'rgba(239, 68, 68, 0.1)'
          : isUrgent
            ? 'rgba(245, 158, 11, 0.1)'
            : 'rgba(59, 130, 246, 0.08)',
        border: `2px solid ${
          isExpired ? '#ef4444' : isUrgent ? '#f59e0b' : '#3b82f6'
        }`,
        transition: 'all 0.3s ease',
      }}
    >
      <Timer
        size={22}
        style={{
          color: isExpired ? '#ef4444' : isUrgent ? '#f59e0b' : '#3b82f6',
          animation: isUrgent && !isExpired ? 'pulse-timer 1s infinite' : 'none',
        }}
      />
      <div style={{ textAlign: 'center' }}>
        <div
          data-testid="countdown-display"
          style={{
            fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
            fontSize: '1.75rem',
            fontWeight: 700,
            letterSpacing: '0.1em',
            color: isExpired ? '#ef4444' : isUrgent ? '#d97706' : '#1e40af',
            lineHeight: 1,
          }}
        >
          {isExpired ? '00:00' : `${minutes}:${seconds}`}
        </div>
        <div
          style={{
            fontSize: '0.75rem',
            fontWeight: 600,
            color: isExpired ? '#ef4444' : '#64748b',
            marginTop: '0.25rem',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
          }}
        >
          {isExpired
            ? 'Reservierung abgelaufen'
            : isUrgent
              ? 'Bitte jetzt bestätigen!'
              : 'Reservierung läuft ab'}
        </div>
      </div>

      <style>{`
        @keyframes pulse-timer {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
      `}</style>
    </div>
  );
};

export default BookingCountdown;
